import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface EnterpriseNavItem {
  icon: LucideIcon;
  label: string;
  to: string;
}

interface EnterpriseNavProps {
  items: EnterpriseNavItem[];
  /** Optional right-aligned slot (e.g. a shared partner selector). */
  right?: ReactNode;
}

/**
 * Monochrome underline sub-nav for the `/enterprise` shell. Route-based
 * NavLinks (URL-addressable — NOT shadcn Tabs) so each section stays
 * deep-linkable. Active tab gets a solid foreground underline; the section is
 * named once on the left ("Enterprise") so child page headers don't have to
 * repeat it.
 */
export function EnterpriseNav({ items, right }: EnterpriseNavProps): JSX.Element {
  return (
    <nav className="flex items-center gap-1 border-b border-border bg-background px-4 h-11">
      <span className="mr-4 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Enterprise
      </span>
      {items.map(({ icon: Icon, label, to }) => (
        <NavLink
          key={to}
          to={to}
          end
          className={({ isActive }) =>
            cn(
              'inline-flex items-center gap-1.5 h-11 px-3 -mb-px border-b-2 text-sm font-medium transition-colors',
              isActive
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
            )
          }
        >
          <Icon className="h-4 w-4" />
          {label}
        </NavLink>
      ))}
      {right && <div className="ml-auto">{right}</div>}
    </nav>
  );
}
